/**
 * @file: leadListConstants.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Static option tables for the lead-list controls (status tabs, sort orders,
 * date presets). Kept separate from the hook so both the hook and the
 * presentational pills/sheets read from one source of truth.
 */

import {
  LeadIntention,
  LeadIntentionLabel,
  LeadOrigin,
  LeadOriginLabel,
  LeadStatus,
} from '@euler/model/lead/Lead';
import { LeadSorter } from '@euler/model/lead/types';

export type LeadStatusTab = {
  key: string;
  label: string;
  status?: LeadStatus[];
};

// The status filter tabs. `全部` clears the status filter; the rest map a tab
// to the underlying lead statuses it groups.
export const kLeadStatusTabs: LeadStatusTab[] = [
  { key: 'all', label: '全部' },
  { key: 'todo', label: '待跟进', status: ['pending', 'due'] },
  { key: 'working', label: '跟进中', status: ['working'] },
  { key: 'booked', label: '已预约', status: ['booked'] },
  { key: 'converted', label: '已转化', status: ['converted'] },
  { key: 'closed', label: '已结束', status: ['lost', 'cancelled', 'expired'] },
];

export type LeadSortOption = {
  key: string;
  label: string;
  sorter: LeadSorter;
};

export const kLeadSortOptions: LeadSortOption[] = [
  {
    key: 'recentReach',
    label: '最近跟进',
    sorter: { prop: 'lastReachAt', dir: 'desc' },
  },
  {
    key: 'targetDate',
    label: '目标日期',
    sorter: { prop: 'targetServiceDate', dir: 'asc' },
  },
  {
    key: 'createdAt',
    label: '创建时间',
    sorter: { prop: 'createdAt', dir: 'desc' },
  },
];

// Which date column the range filter targets. The keys match the range fields
// on `LeadListFilter` so the chosen field can be assigned by key.
export type LeadDateField =
  | 'targetServiceDate'
  | 'createdAt'
  | 'lastReachAt'
  | 'lastActivityAt';

export const kLeadDateFields: { key: LeadDateField; label: string }[] = [
  { key: 'targetServiceDate', label: '目标日期' },
  { key: 'createdAt', label: '创建日期' },
  { key: 'lastReachAt', label: '上次跟进' },
  { key: 'lastActivityAt', label: '上次动态' },
];

// A single-select option group behind one filter pill: `全部` leaves the field
// off the filter entirely, every other entry narrows it to one enum value. The
// backend takes arrays here, so a pick is sent as a one-element list.
export type LeadValueOption<T> = {
  key: string;
  label: string;
  values?: T[];
};

// 线索来源 — how the lead entered the system. Mirrors the card's subtitle, so
// both read the same label table.
export const kLeadOriginOptions: LeadValueOption<LeadOrigin>[] = [
  { key: 'all', label: '全部' },
  { key: 'system', label: LeadOriginLabel.system, values: ['system'] },
  {
    key: 'direct_call',
    label: LeadOriginLabel.direct_call,
    values: ['direct_call'],
  },
  {
    key: 'online_contact',
    label: LeadOriginLabel.online_contact,
    values: ['online_contact'],
  },
  { key: 'walk_in', label: LeadOriginLabel.walk_in, values: ['walk_in'] },
  { key: 'referral', label: LeadOriginLabel.referral, values: ['referral'] },
  { key: 'campaign', label: LeadOriginLabel.campaign, values: ['campaign'] },
  { key: 'other', label: LeadOriginLabel.other, values: ['other'] },
];

export const kLeadIntentionOptions: LeadValueOption<LeadIntention>[] = [
  { key: 'all', label: '全部' },
  { key: 'high', label: LeadIntentionLabel.high, values: ['high'] },
  { key: 'medium', label: LeadIntentionLabel.medium, values: ['medium'] },
  { key: 'low', label: LeadIntentionLabel.low, values: ['low'] },
  { key: 'none', label: LeadIntentionLabel.none, values: ['none'] },
  {
    key: 'undetermined',
    label: LeadIntentionLabel.undetermined,
    values: ['undetermined'],
  },
];

export type LeadDatePresetKey =
  | 'all'
  | 'thisMonth'
  | 'lastMonth'
  | 'last7'
  | 'custom';

export const kLeadDatePresets: { key: LeadDatePresetKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'thisMonth', label: '本月' },
  { key: 'lastMonth', label: '上月' },
  { key: 'last7', label: '近7天' },
  { key: 'custom', label: '自定义' },
];
